/**
 * React Query hooks for the Advertising Optimization arche. All server
 * state flows through these (React Query v5 conventions per
 * docs/react-query-patterns.md); mutations invalidate the narrowest
 * relevant key.
 */

import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type AdoptCreateCampaignRequest,
  type AdoptCreateCreativeRequest,
  type AdoptCreateExperimentRequest,
  type AdoptCreateLinkRequest,
  type AdoptBidEstimatesRequest,
  type AdoptCreatePlacementRequest,
  type AdoptEditPlacementRequest,
  type AdoptFunnelGoalInput,
  type AdoptGenerationDto,
  type AdoptPutDailyAdLimitRequest,
  type AdoptPutSpendCapRequest,
  type AdoptPutTrialSettingsRequest,
  type AdoptSpendCapParam,
  type AdoptPutSettingsRequest,
  type AdoptRsaContentDto,
  type AdoptStrategyDto,
  type AdoptUpdateCampaignRequest,
} from '@/lib/api';
import { adoptQueryKeys } from '../api/queryKeys';

// =============================================================================
// Campaigns
// =============================================================================

export function useAdoptCampaigns(
  holderId: string | null,
  filters: { status?: string; promotedArcheId?: string } = {},
) {
  return useQuery({
    queryKey: adoptQueryKeys.campaignsList(holderId ?? '', filters),
    queryFn: () => api.adoptListCampaigns(holderId!, filters),
    enabled: !!holderId,
    select: (data) => data.campaigns,
  });
}

/** The dashboard's Performance section; refreshed when the window or campaign changes. */
export function useAdoptPerformanceReport(
  holderId: string | null,
  days: number,
  campaignId: string | null,
) {
  return useQuery({
    queryKey: adoptQueryKeys.performance(holderId ?? '', days, campaignId),
    queryFn: () => api.adoptGetPerformanceReport(holderId!, { days, campaignId }),
    enabled: !!holderId,
  });
}

export function useAdoptCampaign(holderId: string | null, campaignId: string | undefined) {
  return useQuery({
    queryKey: adoptQueryKeys.campaign(holderId ?? '', campaignId ?? ''),
    queryFn: () => api.adoptGetCampaign(holderId!, campaignId!),
    enabled: !!holderId && !!campaignId,
  });
}

export function useCreateAdoptCampaign(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptCreateCampaignRequest) => api.adoptCreateCampaign(holderId!, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.campaigns(holderId ?? '') });
    },
  });
}

export function useUpdateAdoptCampaign(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptUpdateCampaignRequest) =>
      api.adoptUpdateCampaign(holderId!, campaignId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.campaigns(holderId ?? '') });
    },
  });
}

export function useAdoptCampaignLifecycle(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (verb: 'activate' | 'pause' | 'complete' | 'archive') =>
      api.adoptCampaignLifecycle(holderId!, campaignId, verb),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.campaigns(holderId ?? '') });
      // pause / complete / archive also pause the campaign's placements.
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.placements(holderId ?? '', campaignId),
      });
    },
  });
}

export function useAdoptKillSwitch(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.adoptSetKillSwitch(holderId!, campaignId, enabled),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.campaigns(holderId ?? '') });
      // The switch pauses every deployed placement (and releasing it makes
      // them resumable again).
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.placements(holderId ?? '', campaignId),
      });
    },
  });
}

export function usePutAdoptFunnelGoals(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (goals: AdoptFunnelGoalInput[]) =>
      api.adoptPutFunnelGoals(holderId!, campaignId, goals),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.campaign(holderId ?? '', campaignId),
      });
    },
  });
}

export function usePutAdoptStrategy(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (strategy: AdoptStrategyDto) =>
      api.adoptPutStrategy(holderId!, campaignId, strategy),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.campaigns(holderId ?? '') });
    },
  });
}

/**
 * The campaign's guest trials (owner / admin only). Pessimistic: the card shows
 * what the server saved. Refreshes the campaign (its version moved) and the
 * auto-unlock guardrails, which summarize every campaign's trials.
 */
export function usePutAdoptTrialSettings(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptPutTrialSettingsRequest) =>
      api.adoptPutTrialSettings(holderId!, campaignId, body),
    onSuccess: (data) => {
      // The server's answer, shown at once (the refetch below confirms it).
      queryClient.setQueryData<Awaited<ReturnType<typeof api.adoptGetCampaign>>>(
        adoptQueryKeys.campaign(holderId ?? '', campaignId),
        (old) => (old ? { ...old, campaign: data.campaign, trials: data.trials } : old),
      );
    },
    onSettled: () => {
      // Also on a 409: the campaign moved (e.g. the sweep suspended it), so the
      // card must show the newer state before the person tries again.
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.campaign(holderId ?? '', campaignId),
      });
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.autoUnlock(holderId ?? '') });
    },
  });
}

// =============================================================================
// Settings
// =============================================================================

export function useAdoptSettings(holderId: string | null) {
  return useQuery({
    queryKey: adoptQueryKeys.settings(holderId ?? ''),
    queryFn: () => api.adoptGetSettings(holderId!),
    enabled: !!holderId,
    select: (data) => data.settings,
  });
}

export function usePutAdoptSettings(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptPutSettingsRequest) => api.adoptPutSettings(holderId!, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.settings(holderId ?? '') });
    },
  });
}

// =============================================================================
// Workspace daily ad limit (human-only: a browser session, owner / admin)
// =============================================================================

export function useAdoptDailyAdLimit(holderId: string | null) {
  return useQuery({
    queryKey: adoptQueryKeys.dailyAdLimit(holderId ?? ''),
    queryFn: () => api.adoptGetDailyAdLimit(holderId!),
    enabled: !!holderId,
    select: (data) => data.dailyAdLimit,
  });
}

export function useAdoptDailyDigest(holderId: string | null) {
  return useQuery({
    queryKey: adoptQueryKeys.dailyDigest(holderId ?? ''),
    queryFn: () => api.adoptGetDailyDigest(holderId!),
    enabled: !!holderId,
    select: (data) => data.dailyDigest,
  });
}

export function usePutAdoptDailyDigest(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.adoptPutDailyDigest(holderId!, enabled),
    onSuccess: (data) => {
      queryClient.setQueryData(adoptQueryKeys.dailyDigest(holderId ?? ''), data);
    },
  });
}

export function usePutAdoptDailyAdLimit(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptPutDailyAdLimitRequest) => api.adoptPutDailyAdLimit(holderId!, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.dailyAdLimit(holderId ?? '') });
      // A lower below what runs pauses placements.
      void queryClient.invalidateQueries({
        queryKey: [...adoptQueryKeys.all, 'placements', holderId ?? ''],
      });
    },
  });
}

// =============================================================================
// Optional spend caps (human-only: a browser session, owner / admin)
// =============================================================================

export function useAdoptSpendCaps(holderId: string | null) {
  return useQuery({
    queryKey: adoptQueryKeys.spendCaps(holderId ?? ''),
    queryFn: () => api.adoptGetSpendCaps(holderId!),
    enabled: !!holderId,
    select: (data) => data.spendCaps,
  });
}

export function usePutAdoptSpendCap(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cap, body }: { cap: AdoptSpendCapParam; body: AdoptPutSpendCapRequest }) =>
      api.adoptPutSpendCap(holderId!, cap, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.spendCaps(holderId ?? '') });
      // A cap at or below what was spent pauses placements.
      void queryClient.invalidateQueries({
        queryKey: [...adoptQueryKeys.all, 'placements', holderId ?? ''],
      });
    },
  });
}

/** Placement changes move what is committed against the daily ad limit. */
function invalidatePlacementsAndLimit(
  queryClient: ReturnType<typeof useQueryClient>,
  holderId: string | null,
  campaignId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: adoptQueryKeys.placements(holderId ?? '', campaignId),
  });
  void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.dailyAdLimit(holderId ?? '') });
}

// =============================================================================
// Tracked links
// =============================================================================

export function useAdoptLinks(holderId: string | null) {
  return useQuery({
    queryKey: adoptQueryKeys.links(holderId ?? ''),
    queryFn: () => api.adoptListLinks(holderId!, { status: 'active' }),
    enabled: !!holderId,
    select: (data) => data.links,
  });
}

export function useCreateAdoptLink(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptCreateLinkRequest) => api.adoptCreateLink(holderId!, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.links(holderId ?? '') });
    },
  });
}

export function useArchiveAdoptLink(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => api.adoptArchiveLink(holderId!, linkId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.links(holderId ?? '') });
    },
  });
}

// =============================================================================
// Goals
// =============================================================================

export function useAdoptGoals(holderId: string | null) {
  return useQuery({
    queryKey: [...adoptQueryKeys.all, 'goals', holderId ?? ''],
    queryFn: () => api.adoptListGoals(holderId!),
    enabled: !!holderId,
    select: (data) => data.goals,
  });
}

// =============================================================================
// Channel accounts + placements
// =============================================================================

export function useAdoptAccounts(holderId: string | null) {
  return useQuery({
    queryKey: adoptQueryKeys.accounts(holderId ?? ''),
    queryFn: () => api.adoptListAccounts(holderId!),
    enabled: !!holderId,
    select: (data) => data.accounts,
  });
}

/**
 * Starts the BYO OAuth flow. Deliberately NOT a navigation side effect inside
 * the hook: the caller decides when to leave the page, so a failed init shows
 * an error instead of a blank redirect.
 */
export function useAdoptOAuthInit(holderId: string | null) {
  return useMutation({
    mutationFn: (targetCustomerId?: string) => api.adoptOAuthInit(holderId!, targetCustomerId),
  });
}

export function useAdoptDisconnectAccount(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => api.adoptDisconnectAccount(holderId!, accountId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adoptQueryKeys.accounts(holderId ?? '') });
    },
  });
}

export function useAdoptPlacements(holderId: string | null, campaignId: string | undefined) {
  return useQuery({
    queryKey: adoptQueryKeys.placements(holderId ?? '', campaignId ?? ''),
    queryFn: () => api.adoptListPlacements(holderId!, campaignId!),
    enabled: !!holderId && !!campaignId,
    select: (data) => data.placements,
  });
}

export function useCreateAdoptPlacement(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptCreatePlacementRequest) =>
      api.adoptCreatePlacement(holderId!, campaignId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.placements(holderId ?? '', campaignId),
      });
    },
  });
}

export function useEditAdoptPlacement(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { placementId: string; body: AdoptEditPlacementRequest }) =>
      api.adoptEditPlacement(holderId!, input.placementId, input.body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.placements(holderId ?? '', campaignId),
      });
    },
  });
}

export function useDeployAdoptPlacement(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (placementId: string) => api.adoptDeployPlacement(holderId!, placementId),
    onSuccess: () => invalidatePlacementsAndLimit(queryClient, holderId, campaignId),
  });
}

export function useAdoptPlacementLifecycle(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      placementId,
      verb,
      acknowledgeBelowMarketBid,
    }: {
      placementId: string;
      verb: 'pause' | 'resume';
      acknowledgeBelowMarketBid?: boolean;
    }) =>
      acknowledgeBelowMarketBid
        ? api.adoptPlacementLifecycle(holderId!, placementId, verb, {
            acknowledgeBelowMarketBid: true,
          })
        : api.adoptPlacementLifecycle(holderId!, placementId, verb),
    onSuccess: () => invalidatePlacementsAndLimit(queryClient, holderId, campaignId),
  });
}

/** The form's "Check market bids": keyword-planner data before saving. */
export function useAdoptKeywordBidEstimates(holderId: string | null) {
  return useMutation({
    mutationFn: (body: AdoptBidEstimatesRequest) => api.adoptEstimateKeywordBids(holderId!, body),
  });
}

// =============================================================================
// Creatives + generation
// =============================================================================

export function useAdoptCreatives(
  holderId: string | null,
  campaignId: string | undefined,
  filters: { status?: string } = {},
) {
  return useQuery({
    queryKey: [...adoptQueryKeys.creatives(holderId ?? '', campaignId ?? ''), filters],
    queryFn: () => api.adoptListCreatives(holderId!, campaignId!, filters),
    enabled: !!holderId && !!campaignId,
    select: (data) => data.creatives,
  });
}

export function useCreateAdoptCreative(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptCreateCreativeRequest) =>
      api.adoptCreateCreative(holderId!, campaignId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.creatives(holderId ?? '', campaignId),
      });
    },
  });
}

export function useUpdateAdoptCreative(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ creativeId, content }: { creativeId: string; content: AdoptRsaContentDto }) =>
      api.adoptUpdateCreative(holderId!, creativeId, content),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.creatives(holderId ?? '', campaignId),
      });
    },
  });
}

export function useAdoptCreativeAction(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      creativeId,
      verb,
    }: {
      creativeId: string;
      verb: 'approve' | 'reject' | 'pause' | 'resume';
    }) => api.adoptCreativeAction(holderId!, creativeId, verb),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.creatives(holderId ?? '', campaignId),
      });
    },
  });
}

export function useDeployAdoptCreative(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      creativeId,
      placementId,
      experimentId,
      variantKey,
    }: {
      creativeId: string;
      placementId?: string;
      experimentId?: string;
      variantKey?: string;
    }) => api.adoptDeployCreative(holderId!, creativeId, { placementId, experimentId, variantKey }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.creatives(holderId ?? '', campaignId),
      });
    },
  });
}

export function useKillAdoptCreative(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ creativeId, reason }: { creativeId: string; reason: string }) =>
      api.adoptKillCreative(holderId!, creativeId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.creatives(holderId ?? '', campaignId),
      });
    },
  });
}

/**
 * How often the generation list is re-read while any generation is running.
 * Copy and challengers run in the background (the POST answers 202 with the
 * running row); a run takes ~15–60 seconds.
 */
export const ADOPT_GENERATION_POLL_MS = 3000;

/**
 * Puts a just-started (running) generation at the top of the campaign's
 * generation list, so the list polls from the first render instead of after
 * the next refetch.
 */
function seedRunningGeneration(
  queryClient: ReturnType<typeof useQueryClient>,
  holderId: string | null,
  campaignId: string,
  generation: AdoptGenerationDto,
): void {
  queryClient.setQueryData<{ generations: AdoptGenerationDto[] }>(
    adoptQueryKeys.generations(holderId ?? '', campaignId),
    (prev) => ({
      generations: [generation, ...(prev?.generations ?? []).filter((g) => g.id !== generation.id)],
    }),
  );
  void queryClient.invalidateQueries({
    queryKey: adoptQueryKeys.generations(holderId ?? '', campaignId),
  });
}

/** Starts an ad-copy generation (202 — it runs in the background; see useAdoptGenerations). */
export function useGenerateAdoptCopy(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      count?: number;
      angles?: string[];
      instructions?: string;
      placementId?: string;
      landingVariantKey?: string;
    }) => api.adoptGenerateCopy(holderId!, campaignId, body),
    onSuccess: (data) => seedRunningGeneration(queryClient, holderId, campaignId, data.generation),
  });
}

/** Starts a challenger generation (202, like useGenerateAdoptCopy). */
export function useGenerateAdoptChallengers(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { count?: number; placementId?: string }) =>
      api.adoptGenerateChallengers(holderId!, campaignId, body),
    onSuccess: (data) => seedRunningGeneration(queryClient, holderId, campaignId, data.generation),
  });
}

export function useGenerateAdoptKeywords(holderId: string | null, campaignId: string) {
  return useMutation({
    mutationFn: (body: { seedTerms?: string[]; count?: number }) =>
      api.adoptGenerateKeywords(holderId!, campaignId, body),
  });
}

/**
 * The campaign's generations, newest first. Polls every
 * {@link ADOPT_GENERATION_POLL_MS} while any is running (the server fails a
 * run that died after ~6 minutes, so this never polls forever), and refreshes
 * the creatives list when a running generation finishes — that is when its
 * drafts appear.
 */
export function useAdoptGenerations(holderId: string | null, campaignId: string | undefined) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: adoptQueryKeys.generations(holderId ?? '', campaignId ?? ''),
    queryFn: () => api.adoptListGenerations(holderId!, campaignId!),
    enabled: !!holderId && !!campaignId,
    select: (data) => data.generations,
    refetchInterval: (q) =>
      (q.state.data?.generations ?? []).some((g) => g.status === 'running')
        ? ADOPT_GENERATION_POLL_MS
        : false,
  });

  const running = useRef<Set<string>>(new Set());
  const generations = query.data;
  useEffect(() => {
    if (!generations) return;
    const finished = generations.some((g) => g.status !== 'running' && running.current.has(g.id));
    running.current = new Set(generations.filter((g) => g.status === 'running').map((g) => g.id));
    if (finished) {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.creatives(holderId ?? '', campaignId ?? ''),
      });
    }
  }, [generations, queryClient, holderId, campaignId]);

  return query;
}

// =============================================================================
// Action Inbox
// =============================================================================

export function useAdoptActions(
  holderId: string | null,
  filters: { status?: string; campaignId?: string } = {},
) {
  return useQuery({
    queryKey: adoptQueryKeys.actionsList(holderId ?? '', filters),
    queryFn: () => api.adoptListActions(holderId!, filters),
    enabled: !!holderId,
    select: (data) => data.actions,
    // Proposals arrive on the hourly optimize sweep; poll lazily.
    refetchInterval: 60_000,
  });
}

export function useReviewAdoptAction(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ actionId, verb }: { actionId: string; verb: 'approve' | 'reject' }) =>
      api.adoptReviewAction(holderId!, actionId, verb),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.actions(holderId ?? '') });
      // Approvals mutate campaigns/placements/creatives downstream.
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.campaigns(holderId ?? '') });
    },
  });
}

export function useAdoptAutoUnlock(holderId: string | null) {
  return useQuery({
    queryKey: adoptQueryKeys.autoUnlock(holderId ?? ''),
    queryFn: () => api.adoptGetAutoUnlock(holderId!),
    enabled: !!holderId,
    select: (data) => data.autoUnlock,
  });
}

/** Unlock status AND the guardrails an approval accepts (same cache entry). */
export function useAdoptAutoUnlockReview(holderId: string | null) {
  return useQuery({
    queryKey: adoptQueryKeys.autoUnlock(holderId ?? ''),
    queryFn: () => api.adoptGetAutoUnlock(holderId!),
    enabled: !!holderId,
  });
}

/** Platform admin: approve automatic mode for the workspace. */
export function useGrantAdoptAutoUnlock(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => api.adoptAdminGrantAutoUnlock(holderId!, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.autoUnlock(holderId ?? '') });
    },
  });
}

/** Platform admin: withdraw the approval. */
export function useRevokeAdoptAutoUnlock(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.adoptAdminRevokeAutoUnlock(holderId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.autoUnlock(holderId ?? '') });
    },
  });
}

// =============================================================================
// Access — who may use AdOpt in a workspace
// =============================================================================

/** The caller's standing in the workspace (allowed / canWrite / canManageSpend). */
export function useAdoptAccess(holderId: string | null) {
  return useQuery({
    queryKey: adoptQueryKeys.access(holderId ?? ''),
    queryFn: () => api.adoptGetAccess(holderId!),
    enabled: !!holderId,
    select: (data) => data.access,
  });
}

/** Platform admin: the grant opening AdOpt to this org workspace, or null. */
export function useAdoptWorkspaceAccess(holderId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: adoptQueryKeys.workspaceAccess(holderId ?? ''),
    queryFn: () => api.adoptAdminGetWorkspaceAccess(holderId!),
    enabled: !!holderId && enabled,
    select: (data) => data.workspaceAccess,
  });
}

function invalidateWorkspaceAccess(
  queryClient: ReturnType<typeof useQueryClient>,
  holderId: string | null,
): void {
  void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.workspaceAccess(holderId ?? '') });
  void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.access(holderId ?? '') });
}

/** Platform admin: open AdOpt to the workspace's members. */
export function useGrantAdoptWorkspaceAccess(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => api.adoptAdminGrantWorkspaceAccess(holderId!, note),
    onSuccess: () => invalidateWorkspaceAccess(queryClient, holderId),
  });
}

/** Platform admin: close it again. */
export function useRevokeAdoptWorkspaceAccess(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.adoptAdminRevokeWorkspaceAccess(holderId!),
    onSuccess: () => invalidateWorkspaceAccess(queryClient, holderId),
  });
}

// =============================================================================
// Experiments
// =============================================================================

export function useAdoptExperiments(holderId: string | null) {
  return useQuery({
    queryKey: adoptQueryKeys.experiments(holderId ?? ''),
    queryFn: () => api.adoptListExperiments(holderId!),
    enabled: !!holderId,
    select: (data) => data.experiments,
  });
}

export function useAdoptExperiment(holderId: string | null, experimentId: string | undefined) {
  return useQuery({
    queryKey: adoptQueryKeys.experiment(holderId ?? '', experimentId ?? ''),
    queryFn: () => api.adoptGetExperiment(holderId!, experimentId!),
    enabled: !!holderId && !!experimentId,
  });
}

export function useAdoptExperimentResults(
  holderId: string | null,
  experimentId: string | undefined,
) {
  return useQuery({
    queryKey: adoptQueryKeys.experimentResults(holderId ?? '', experimentId ?? ''),
    queryFn: () => api.adoptGetExperimentResults(holderId!, experimentId!),
    enabled: !!holderId && !!experimentId,
    // Posteriors move on the hourly stats sweep; poll lazily.
    refetchInterval: 60_000,
  });
}

export function useCreateAdoptExperiment(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptCreateExperimentRequest) => api.adoptCreateExperiment(holderId!, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.experiments(holderId ?? '') });
    },
  });
}

export function useAdoptExperimentLifecycle(holderId: string | null, experimentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (verb: 'start' | 'stop' | 'complete' | 'archive') =>
      api.adoptExperimentLifecycle(holderId!, experimentId, verb),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.experiments(holderId ?? '') });
    },
  });
}
